import { supabase } from '../lib/supabase';
import { logAuditEvent } from './auditLogService';

/**
 * Stripe Connect Service — Destination Charge Model
 *
 * Handles expert Connect account management and payouts.
 * Funds go directly to expert Connect accounts via destination charges.
 * No platform wallet — experts request bank payouts from their Connect balance.
 */

/**
 * Extract a human-readable error message from a Supabase FunctionsHttpError
 */
async function extractFunctionError(error) {
  if (error?.context) {
    try {
      const body = await error.context.json();
      if (body?.error) return body.error;
    } catch (_) { /* ignore parse errors */ }
  }
  return error?.message || 'Unknown error';
}

/**
 * Create Stripe Connect Express account for expert
 * Minimal info required (email, country). KYC collected at payout time.
 */
export async function createConnectAccount(expertId, email, fullName, country) {
  try {
    const { data, error } = await supabase?.functions?.invoke('create-connect-account', {
      body: { expertId, email, fullName, country }
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);

    logAuditEvent({ actionType: 'payment.connect_account_created', actionDescription: `Stripe Connect account created`, entityType: 'expert_profile', entityId: expertId, severity: 'warning' }).catch(console.error);
    return { success: true, accountId: data?.accountId, data: data?.connectData };
  } catch (error) {
    const message = await extractFunctionError(error);
    console.error('Create Connect account error:', message);
    return { success: false, error: message };
  }
}

/**
 * Create Connect account onboarding link
 */
export async function createAccountLink(accountId, expertId) {
  try {
    const { data, error } = await supabase?.functions?.invoke('create-account-link', {
      body: {
        accountId,
        expertId,
        refreshUrl: `${window.location?.origin}/expert-dashboard?refresh=true`,
        returnUrl: `${window.location?.origin}/expert-dashboard?onboarding=complete`
      }
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);

    return { success: true, url: data?.url };
  } catch (error) {
    const message = await extractFunctionError(error);
    console.error('Create account link error:', message);
    return { success: false, error: message };
  }
}

/**
 * Retrieve Connect account details
 */
export async function getConnectAccount(accountId) {
  try {
    const { data, error } = await supabase?.functions?.invoke('get-connect-account', {
      body: { accountId }
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);

    return { success: true, account: data?.account };
  } catch (error) {
    const message = await extractFunctionError(error);
    console.error('Get Connect account error:', message);
    return { success: false, error: message };
  }
}

/**
 * Sync Connect account status from Stripe
 */
export async function syncConnectAccountStatus(expertId) {
  try {
    const { data: expert, error: expertError } = await supabase
      ?.from('expert_profiles')
      ?.select('stripe_connect_account_id')
      ?.eq('id', expertId)
      ?.single();

    if (expertError) throw expertError;

    let accountId = expert?.stripe_connect_account_id;

    if (!accountId) {
      const { data: sca } = await supabase
        ?.from('stripe_connect_accounts')
        ?.select('stripe_account_id')
        ?.eq('expert_id', expertId)
        ?.maybeSingle();
      accountId = sca?.stripe_account_id;
    }

    if (!accountId) {
      return { success: false, error: 'No Connect account found. Please complete Stripe Connect onboarding first.' };
    }

    const accountResult = await getConnectAccount(accountId);
    if (!accountResult?.success) return accountResult;

    const account = accountResult?.account;

    await supabase
      ?.from('stripe_connect_accounts')
      ?.update({
        charges_enabled: account?.charges_enabled,
        payouts_enabled: account?.payouts_enabled,
        details_submitted: account?.details_submitted,
        verification_status: account?.requirements?.disabled_reason || 'verified',
        verification_fields_needed: account?.requirements?.currently_due || [],
        account_data: account
      })
      ?.eq('stripe_account_id', account?.id);

    await supabase
      ?.from('expert_profiles')
      ?.update({
        stripe_connect_account_id: account?.id,
        connect_onboarding_completed: account?.details_submitted,
        connect_charges_enabled: account?.charges_enabled,
        connect_payouts_enabled: account?.payouts_enabled
      })
      ?.eq('id', expertId);

    return { success: true, account };
  } catch (error) {
    console.error('Sync Connect account error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get expert payout history
 */
export async function getExpertPayouts(expertId) {
  try {
    const { data, error } = await supabase
      ?.from('payouts')
      ?.select(`
        *,
        contract:marketplace_contracts(title, contract_type)
      `)
      ?.eq('expert_id', expertId)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    return { success: true, payouts: data };
  } catch (error) {
    console.error('Get expert payouts error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get the expert's Stripe Connect balance.
 * 'available' = funds ready to withdraw to bank now.
 * 'pending'   = funds settling (recently received from destination charge).
 */
export async function getConnectBalance(expertId) {
  try {
    console.log('[getConnectBalance] calling edge function for expertId:', expertId);
    const { data, error } = await supabase?.functions?.invoke('get-connect-balance', {
      body: { expertId },
    });

    console.log('[getConnectBalance] raw edge function response:', JSON.stringify({ data, error: error?.message ?? null }));

    if (error) throw error;
    if (data?.error) throw new Error(data.error);

    const result = {
      success: true,
      available:        data?.available        ?? 0,
      pending:          data?.pending          ?? 0,   // settling (released escrow)
      heldInEscrow:     data?.heldInEscrow     ?? 0,   // locked escrow
      totalGross:       data?.totalGross       ?? 0,
      currency:         data?.currency         ?? 'usd',
      hasConnectAccount: data?.hasConnectAccount ?? false,
      connectAccountId:  data?.connectAccountId  ?? null,
    };
    console.log('[getConnectBalance] parsed result:', JSON.stringify(result));
    return result;
  } catch (error) {
    const message = await extractFunctionError(error);
    console.error('[getConnectBalance] error:', message);
    return { success: false, error: message, available: 0, pending: 0, heldInEscrow: 0, totalGross: 0 };
  }
}

/**
 * Request a bank payout from the expert's Connect balance.
 * Flow: Expert's Connect balance → Expert's bank account.
 *
 * Before payout, the edge function checks account.requirements.currently_due:
 *   - If incomplete → returns onboarding link + blocks payout
 *   - If complete → creates payout on the connected account
 *
 * @param {string} expertId
 * @param {number} amount - dollars
 * @param {'standard'|'instant'} method
 */
export async function requestPayout(expertId, amount, method = 'standard') {
  try {
    const { data, error } = await supabase?.functions?.invoke('request-payout', {
      body: { expertId, amount, method },
    });
    if (error) {
      const msg = await extractFunctionError(error);
      throw new Error(msg);
    }
    if (data?.error && !data?.requiresOnboarding) throw new Error(data.error);

    // If onboarding is required, return the link
    if (data?.requiresOnboarding) {
      return {
        success: false,
        requiresOnboarding: true,
        onboardingUrl: data?.onboardingUrl,
        currentlyDue: data?.currentlyDue,
        error: data?.error || 'Please complete your account setup before requesting a payout.',
      };
    }

    logAuditEvent({ actionType: 'payment.payout_requested', actionDescription: `Payout of $${amount} via ${method} requested`, entityType: 'expert_profile', entityId: expertId, metadata: { amount, method, payoutId: data?.payoutId }, severity: 'warning' }).catch(console.error);
    return {
      success: true,
      payoutId: data?.payoutId,
      status: data?.status,
      estimatedArrival: data?.estimatedArrival,
      method: data?.method,
    };
  } catch (error) {
    const message = await extractFunctionError(error);
    console.error('Request payout error:', message);
    return { success: false, error: message };
  }
}

/**
 * Process mock payout for test accounts
 */
export async function processExpertPayout(expertId, amount, contractId, description) {
  try {
    const { data: expertProfile } = await supabase
      ?.from('user_profiles')
      ?.select('is_test_account')
      ?.eq('id', expertId)
      ?.single();

    if (expertProfile?.is_test_account) {
      console.log('Test account - mocking payout');

      const { data: payoutData, error: dbError } = await supabase
        ?.from('payouts')
        ?.insert({
          expert_id: expertId,
          contract_id: contractId,
          gross_amount: amount,
          commission_amount: 0,
          commission_pct: 0,
          net_amount: amount,
          stripe_transfer_id: `test_transfer_${Date.now()}`,
          stripe_connect_account_id: 'acct_test',
          status: 'completed',
          processed_at: new Date().toISOString()
        })
        ?.select()
        ?.single();

      if (dbError) throw dbError;

      return {
        success: true,
        isTestAccount: true,
        transferId: payoutData?.stripe_transfer_id,
        data: payoutData
      };
    }

    // For real accounts, use the request-payout flow
    return await requestPayout(expertId, amount);
  } catch (error) {
    console.error('Process expert payout error:', error);
    return { success: false, error: error?.message };
  }
}
